import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { removeBenchmarkImageV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesImageIdDelete } from '../../generated';

/*
 * DELETE /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images/{image_id}, live behind the API's
 * `custom-benchmarks` flag. Removes the image from the benchmark only; the image itself stays. Tests answer this
 * path with `tests/mocks/benchmark-handlers.ts`.
 */

export interface RemoveBenchmarkImageMutationVariables {
  workspace_id: string;
  benchmark_id: string;
  image_id: string;
}

export type RemoveBenchmarkImageMutation = undefined;

async function removeBenchmarkImage({ workspace_id, benchmark_id, image_id }: RemoveBenchmarkImageMutationVariables) {
  await removeBenchmarkImageV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesImageIdDelete({
    path: { workspace_id, benchmark_id, image_id },
    throwOnError: true,
  });
  return undefined;
}

export const useRemoveBenchmarkImageMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<RemoveBenchmarkImageMutation, TError, RemoveBenchmarkImageMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<RemoveBenchmarkImageMutation, TError, RemoveBenchmarkImageMutationVariables, TContext>({
    mutationKey: ['RemoveBenchmarkImage'],
    mutationFn: removeBenchmarkImage,
    ...options,
  });
};

useRemoveBenchmarkImageMutation.fetcher = (variables: RemoveBenchmarkImageMutationVariables) => () =>
  removeBenchmarkImage(variables);
